/**
 * Bavlio Inc.'s company-wide Google Analytics 4 stream: bavlio.com's stream
 * G-HZZMX0L647 (property 495382659), which rosefall.ai also uses. It runs next
 * to Bavimail's own stream (G-82D8H915MS) and Google Ads tag, which stay as
 * they are.
 *
 * Google's cross-domain measurement needs the same tag ID on every domain
 * (support.google.com/analytics/answer/10071811), so the three sites share
 * this one stream and the linker lists all three: a visit that moves between
 * bavlio.com, rosefall.ai and bavimail.com stays one session with its first
 * source.
 *
 * Set up as bavlio.com sets it up (bavlio-frontend src/lib/analytics.ts):
 * Google signals and ad personalisation off, the address sent with its utm_*
 * parameters only, and nothing sent when the browser asks not to be tracked
 * (Do Not Track or Global Privacy Control). The stream's enhanced measurement
 * is off, so every page view is one sent here: the landing page from the
 * inline Google tag script (it doesn't wait for the app to load), and later
 * in-app navigations from sendBavlioGa4PageView().
 */

export const BAVLIO_GA4_ID = "G-HZZMX0L647";

export const LINKER_DOMAINS = ["bavlio.com", "rosefall.ai", "bavimail.com"] as const;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** Origin, path and utm_* parameters only, as bavlio.com sends them. */
export function bavlioGa4PageLocation(origin: string, pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && /^[A-Za-z0-9._~ -]{1,100}$/.test(value)) kept.set(key, value);
  }
  const query = kept.toString();
  return `${origin}${pathname}${query ? `?${query}` : ""}`;
}

/** The browser asks not to be tracked: Do Not Track or Global Privacy Control. */
export function trackingOptedOut(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.doNotTrack === "1" || nav.globalPrivacyControl === true;
}

/** Must run after `gtag('js', ...)` and before the first `gtag('config', ...)`. */
export const BAVLIO_GA4_LINKER_SCRIPT = `gtag('set', 'linker', { domains: ${JSON.stringify(LINKER_DOMAINS)} });`;

/** Configures the stream and sends the landing page view, unless the browser opted out. */
export const BAVLIO_GA4_CONFIG_SCRIPT = `(function () {
  var n = navigator;
  if (n.doNotTrack === '1' || n.globalPrivacyControl === true) return;
  var q = new URLSearchParams(location.search), k = new URLSearchParams();
  ${JSON.stringify(UTM_KEYS)}.forEach(function (key) {
    var v = q.get(key);
    if (v && /^[A-Za-z0-9._~ -]{1,100}$/.test(v)) k.set(key, v);
  });
  var s = k.toString();
  gtag('config', '${BAVLIO_GA4_ID}', {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: location.origin + location.pathname + (s ? '?' + s : '')
  });
})();`;

/** One page view to the Bavlio stream only, for an in-app navigation to a public page. */
export function sendBavlioGa4PageView(): boolean {
  if (typeof window === "undefined" || trackingOptedOut() || !window.gtag) return false;
  const { origin, pathname, search } = window.location;
  window.gtag("event", "page_view", {
    send_to: BAVLIO_GA4_ID,
    page_location: bavlioGa4PageLocation(origin, pathname, search),
    page_title: document.title,
  });
  return true;
}
